import { useState, useEffect } from 'react';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { HelmetProvider } from 'react-helmet-async';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ApolloProvider } from "@apollo/client";
import apolloClient from "./lib/apollo-client";
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import store, { persistor } from './store';
import { debugCookies } from './utils/cookies';
import Navbar from './components/layout/navbar/Navbar';
// Import your components
import Index from "./pages/Index";
import SignIn from "./pages/auth/SignIn";
import SignUp from "./pages/auth/SignUp";
import Profile from "./pages/Profile";
import Settings from "./pages/Settings";
import NotFound from "./pages/NotFound";
import PrivateRoute from './components/routes/PrivateRoute';
import PublicRoute from './components/routes/PublicRoute';
import Trainings from './pages/Trainings';
import MyTrainings from './pages/MyTrainings';
import About from "./pages/About";
import DashboardLayout from "./components/layout/dashboard/DashboardLayout";
import Dashboard from "./pages/admin/Dashboard";
// import Users from "./pages/admin/Users"; // Not used
import TrainingsManagment  from "./pages/admin/TrainingsManagement";
import Schools from "./pages/admin/Schools";
import UserProfile from './pages/UserProfile';
import TrainingDetails from "@/pages/TrainingDetails";
import Providers from "@/pages/Providers";
import AdminTrainingDetails from "@/pages/admin/TrainingDetails";
import ProvidersManagement from './pages/admin/ProvidersManagement';
import ProviderDetail from '@/pages/admin/ProviderDetail';
import ForgotPassword from "./pages/auth/ForgotPassword";
import ResetPassword from "./pages/auth/ResetPassword";
import VerifyEmail from '@/pages/auth/VerifyEmail';
import EditTraining from "@/pages/admin/EditTraining";
import EditProvider from '@/pages/admin/EditProvider';
import MyProviders from '@/pages/MyProviders';
import ViewMyProviderDetails from '@/pages/admin/providers/ViewMyProviderDetails';
import ViewMyTrainingDetails from '@/pages/admin/trainings/ViewMyTrainingDetails';
import CreateProviderUser from '@/pages/admin/CreateProviderUser';
import Packs from "./pages/Packs";
import UsersEnhanced from './pages/admin/UsersEnhanced';
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";


const queryClient = new QueryClient();



const App = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isStylesLoaded, setIsStylesLoaded] = useState(false);

  useEffect(() => {
    // Set styles as loaded after a short delay
    const timer = setTimeout(() => {
      setIsStylesLoaded(true);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    console.log('=== Checking cookies on app start ===');
    debugCookies();
  }, []);

  if (!isStylesLoaded) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-background">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <Provider store={store}>
      <PersistGate loading={
        <div className="h-screen w-screen flex items-center justify-center">
          Loading...
        </div>
      } persistor={persistor}>
        <ApolloProvider client={apolloClient}>
          <QueryClientProvider client={queryClient}>
            <BrowserRouter>
              <HelmetProvider>
              <TooltipProvider>
                <Navbar
                  isScrolled={isScrolled}
                  isMenuOpen={isMenuOpen}
                  setIsMenuOpen={setIsMenuOpen}
                />
                <div className="pt-16">
                  <Toaster />
                  <Sonner />
                  <Routes>
                    {/* Public routes */}
                    <Route
                      path="/"
                      element={<PublicRoute element={<Index />} />}
                    />
                    <Route
                      path="/signin"
                      element={<PublicRoute element={<SignIn />} restricted />}
                    />
                    <Route
                      path="/signup"
                      element={<PublicRoute element={<SignUp />} restricted />}
                    />
                    <Route
                      path="/courses"
                      element={<PublicRoute element={<Trainings />} />}
                    />
                    {/* Add the new providers route */}
                    <Route
                      path="/providers"
                      element={<PublicRoute element={<Providers />} />}
                    />
                    <Route
                      path="/providers/:id"
                      element={<PublicRoute element={<ProviderDetail />} />}
                    />

                    {/* Protected routes */}
                    <Route
                      path="/profile"
                      element={
                        <PrivateRoute
                          element={<Profile />}
                          allowedRoles={['user', 'admin']}
                        />
                      }
                    />
                    <Route
                      path="/settings"
                      element={
                        <PrivateRoute
                          element={<Settings />}
                          allowedRoles={['user', 'admin']}
                        />
                      }
                    />
                    <Route path="*" element={<NotFound />} />
                    <Route
                      path="/about"
                      element={<About />}
                    />

                    {/* Admin routes */}
                    <Route
                      path="/admin"
                      element={
                        <PrivateRoute
                          element={
                            <DashboardLayout>
                              <Dashboard />
                            </DashboardLayout>
                          }
                          allowedRoles={['user']}
                        />
                      }
                    />
                    <Route
                      path="/admin/users"
                      element={
                        <PrivateRoute
                          element={
                            <DashboardLayout>
                              <UsersEnhanced/>
                            </DashboardLayout>
                          }
                          allowedRoles={['user']}
                        />
                      }
                    />
                    <Route
                      path="/admin/users/create-provider"
                      element={
                        <PrivateRoute
                          element={
                            <DashboardLayout>
                              <CreateProviderUser />
                            </DashboardLayout>
                          }
                          allowedRoles={['user']}
                        />
                      }
                    />
                    <Route
                      path="/admin/trainings"
                      element={
                        <PrivateRoute
                          element={
                            <DashboardLayout>
                              <TrainingsManagment />
                            </DashboardLayout>
                          }
                          allowedRoles={['user']}
                        />
                      }
                    />
                      <Route
                      path="/admin/trainings/:id"
                      element={
                        <PrivateRoute
                          element={
                            <DashboardLayout>
                              <AdminTrainingDetails />
                            </DashboardLayout>
                          }
                          allowedRoles={['user']}
                        />
                      }
                    />
                    <Route
                      path="/my-trainings"
                      element={
                        <PrivateRoute
                          element={
                            <DashboardLayout>
                              <MyTrainings />
                            </DashboardLayout>
                          }
                          allowedRoles={['user', 'instructor']}
                        />
                      }
                    />
                    <Route
                      path="/my-trainings/:id"
                      element={
                        <PrivateRoute
                          element={
                            <DashboardLayout>
                              <ViewMyTrainingDetails />
                            </DashboardLayout>
                          }
                          allowedRoles={['user', 'instructor']}
                        />
                      }
                    />


                    <Route
                      path="/admin/schools"
                      element={
                        <PrivateRoute
                          element={
                            <DashboardLayout>
                              <Schools />
                            </DashboardLayout>
                          }
                          allowedRoles={['user']}
                        />
                      }
                    />

                    {/* User profile route */}
                    <Route
                      path="/users/:id"
                      element={
                        <PrivateRoute
                          element={<UserProfile />}
                          allowedRoles={['user', 'admin']}
                        />
                      }
                    />
                    // Find the routes configuration and add the new route
                    <Route path="/training/:id" element={<TrainingDetails />} />
                    <Route
                      path="/admin/providers"
                      element={
                        <PrivateRoute
                          element={
                            <DashboardLayout>
                              <ProvidersManagement />
                            </DashboardLayout>
                          }
                          allowedRoles={['user']}
                        />
                      }
                    />

                    <Route
                      path="/admin/trainings/:id/edit"
                      element={
                        <PrivateRoute
                          element={
                            <DashboardLayout>
                              <EditTraining />
                            </DashboardLayout>
                          }
                          allowedRoles={['user']}
                        />
                      }
                    />
                    // Inside your Routes component
                    <Route path="/admin/providers/:id"
                      element={
                        <PrivateRoute
                          element={
                            <DashboardLayout>
                              <ProviderDetail />

                            </DashboardLayout>
                          }
                          allowedRoles={['user']}
                        />
                      }
                    />
                    <Route
                      path="/admin/providers/:id/edit"
                      element={
                        <PrivateRoute
                          element={
                            <DashboardLayout>
                              <EditProvider />
                            </DashboardLayout>
                          }
                          allowedRoles={['user']}
                        />
                      }
                    />
                        <Route
                      path="/admin/my-providers"
                      element={
                        <PrivateRoute
                          element={
                            <DashboardLayout>
                              <MyProviders/>
                            </DashboardLayout>
                          }
                          allowedRoles={['user']}
                        />
                      }
                    />
                    <Route
                      path="/admin/my-providers/:id"
                      element={
                        <PrivateRoute
                          element={
                            <DashboardLayout>
                              <ViewMyProviderDetails />
                            </DashboardLayout>
                          }
                          allowedRoles={['user']}
                        />
                      }
                    />


                    <Route path="/packs" element={<Packs />} />

                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password" element={<ResetPassword />} />

                    {/* Legal pages */}
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/privacy" element={<Privacy />} />


                  <Route path='/verify-email' element={<VerifyEmail />} />

                  </Routes>
                </div>
              </TooltipProvider>
              </HelmetProvider>
            </BrowserRouter>
          </QueryClientProvider>
        </ApolloProvider>
      </PersistGate>
    </Provider>
  );
};

export default App;

// Inside your router configuration (likely within createBrowserRouter or Routes component)



